import {nextTick, ref} from "vue";
import useAdvancedRevisionFeedback from "@/composables/approval-center/advanced-revision/useAdvancedRevisionFeedback";

const pendingMerge = ref(null);
const showMergeWarningModal = ref(false);

// Main function to handle highlighting selection
const highlightSelection = async (openFeedback = true) => {
  const {
    openFeedbackTooltip,
    closeFeedbackTooltip,
    createFeedbackItem,
    removeFeedbackItem
  } = useAdvancedRevisionFeedback();

  closeFeedbackTooltip();

  const selection = window.getSelection();
  const selectedText = selection.toString().trim();

  if (selection.rangeCount <= 0 || selectedText.length <= 0) {
    return;
  }

  let range = selection.getRangeAt(0);
  let feedback = '';

  const overlapping = getOverlappingFeedbackItems(range);

  /**
   * If there are multiple overlapped feedback items with the new selection, we warn the user
   * that we're going to combine them all into a single feedback item for the whole selection
   */
  if (overlapping.length > 1) {
    pendingMerge.value = { range, overlapping, selection: selection };
    showMergeWarningModal.value = true;
    return undefined;
  }

  /**
   * If there is only one feedback item overlapped, we extend the selection to include the range for the
   * overlapped feedback item and merge into a single feedback item.
   */
  if (overlapping.length === 1) {
    const firstFeedback = getOverlappedFeedbackItem(overlapping);
    const expandedSelectionRange = getExpandedRangeToIncludeOverlappedFeedbackItems(range, overlapping);
    overlapping.forEach(feedbackItem => {
      removeFeedbackItem(feedbackItem);
    });
    range = expandedSelectionRange;
    feedback = firstFeedback.feedback;
  }

  const feedbackElement = createFeedbackItem(range, feedback);

  selection.removeAllRanges();
  await nextTick();
  if (!openFeedback) {
    return feedbackElement;
  }
  openFeedbackTooltip(feedbackElement);

  return feedbackElement;
};

/**
 * This just ensures if the user highlights anything outside the content element, the selection
 * range will limit the selection to the content element
 */
const limitSelectionToContentElement = (rootElement) => {
  const selection = window.getSelection();
  if (!selection.rangeCount) return;

  const range = selection.getRangeAt(0);
  let { startContainer, startOffset, endContainer, endOffset } = range;

  // Helper to check if a node is a descendant of rootElement
  const isDescendant = (node) => {
    while (node) {
      if (node === rootElement) {
        return true;
      }
      node = node.parentNode;
    }
    return false;
  };

  // If startContainer is not inside rootElement, set to rootElement's first text node
  if (!isDescendant(startContainer)) {
    let node = rootElement;
    while (node.firstChild) node = node.firstChild;
    startContainer = node;
    startOffset = 0;
  }

  // If endContainer is not inside rootElement, set to rootElement's last text node
  if (!isDescendant(endContainer)) {
    let node = rootElement;
    while (node.lastChild) node = node.lastChild;
    if (node.nodeType === 3) {
      endContainer = node;
      endOffset = node.textContent.length;
    } else {
      endContainer = node;
      endOffset = node.childNodes.length;
    }
  }

  // Create a new range limited to rootElement
  const newRange = document.createRange();
  newRange.setStart(startContainer, startOffset);
  newRange.setEnd(endContainer, endOffset);

  selection.removeAllRanges();
  selection.addRange(newRange);
};

const mergeFeedbackItems = async () => {
  const {openFeedbackTooltip, removeFeedbackItem, createFeedbackItem} = useAdvancedRevisionFeedback();

  if (!pendingMerge.value) {
    return;
  }

  const {range, overlapping} = pendingMerge.value;

  // Combine all feedback texts (each on a new line, filter out empty)
  const combinedFeedback = overlapping
    .map(item => item.feedback)
    .filter(feedback => feedback && feedback.trim().length > 0)
    .join('\n');

  overlapping.forEach(removeFeedbackItem);
  const feedbackItem = createFeedbackItem(range, combinedFeedback);

  window.getSelection().removeAllRanges();

  await nextTick();
  openFeedbackTooltip(feedbackItem);

  showMergeWarningModal.value = false;
  pendingMerge.value = null;
};

const cancelMergingFeedbackItems = () => {
  window.getSelection().removeAllRanges();
  showMergeWarningModal.value = false;
  pendingMerge.value = null;
};


/** Helper functions **/
// Loops existing feedback items and returns an array of items that are overlapped by the new selection range.
const getOverlappingFeedbackItems = (selectionRange) => {
  const {feedbackItems} = useAdvancedRevisionFeedback();

  const overlapping = [];
  feedbackItems.value.forEach((item) => {
    const spanRange = getElementRange(item.feedbackElement);
    // Check for overlap using DOM Range boundary comparison
    if (
      selectionRange.compareBoundaryPoints(Range.END_TO_START, spanRange) < 0 &&
      selectionRange.compareBoundaryPoints(Range.START_TO_END, spanRange) > 0
    ) {
      overlapping.push(item);
    }
  });
  return overlapping;
};

// Helper to find the earliest feedback element by DOM position from the provided array of feedback items
const getOverlappedFeedbackItem = (feedbackItems) => {
  if (feedbackItems.length === 0) {
    return null;
  }

  let firstFeedback = feedbackItems[0];
  let firstNode = firstFeedback.feedbackElement;

  feedbackItems.forEach(item => {
    if (isEarlierInDOM(item.feedbackElement, firstNode)) {
      firstFeedback = item;
      firstNode = item.feedbackElement;
    }
  });

  return firstFeedback;
};

// Uses Bitwise comparison to check if node A is earlier in the DOM than node B
const isEarlierInDOM = (nodeA, nodeB) => {
  return nodeA.compareDocumentPosition(nodeB) & Node.DOCUMENT_POSITION_PRECEDING;
};

// Returns the DOM Range of the given element
const getElementRange = (element) => {
  const range = document.createRange();
  range.selectNodeContents(element);
  return range;
};

// Returns a new DOM Range that includes the text from all overlapping feedback items
const getExpandedRangeToIncludeOverlappedFeedbackItems = (selectionRange, overlapping) => {
  let unionRange = selectionRange.cloneRange();

  overlapping.forEach(element => {
    const spanRange = getElementRange(element.feedbackElement);

    if (spanRange.compareBoundaryPoints(Range.START_TO_START, unionRange) < 0) {
      unionRange.setStart(spanRange.startContainer, spanRange.startOffset);
    }

    if (spanRange.compareBoundaryPoints(Range.END_TO_END, unionRange) > 0) {
      unionRange.setEnd(spanRange.endContainer, spanRange.endOffset);
    }
  });

  return unionRange;
};

export default () => ({
  pendingMerge,
  showMergeWarningModal,
  highlightSelection,
  limitSelectionToContentElement,
  mergeFeedbackItems,
  cancelMergingFeedbackItems,
});
